/**
 * Base components for Vue list items with data buffering and list management.
 *
 * This file provides two approaches for creating list item components:
 * 1. Legacy class-based `Item<T, S>` using vue-facing-decorator
 * 2. Modern extends approach using `Item as ItemComponent<T, S>` with Vue Options API
 *
 * @example Legacy Class-based Approach (vue-facing-decorator)
 * ```typescript
 * import { Item } from "@/Control/Vue/Lists/VueList/Items/Item";
 *
 * @Component
 * export class MyItem extends Item<IMyItemType> {
 *   protected defaults = DefaultMyItem;
 * }
 * ```
 *
 * @example Modern Options API Approach
 * ```typescript
 * import Item, { type ItemComponent } from "@/Control/Vue/Lists/VueList/Items/Item";
 *
 * export default defineComponent({
 *   name: 'MyItem',
 *   extends: Item as ItemComponent<IMyItemType>,
 *   data() {
 *     return {
 *       defaults: DefaultMyItem,
 *     };
 *   },
 * });
 * ```
 */
import { defineComponent, type PropType } from "vue";
import { Component, Inject, Prop, Vue, toNative } from "vue-facing-decorator";
import { type IBuffered, isIBuffered } from "@/Control/Vue/Stores/VueList/IBuffered";

type IBufferedWithIdentifier<T, S> = IBuffered<T> & { from: S };

type ItemFrom<T, S> = T | IBuffered<T> | IBufferedWithIdentifier<T, S>;

const hasIdentifier = <T, S>(from: ItemFrom<T, S>): from is IBufferedWithIdentifier<T, S> => {
	return !!from && typeof from === "object" && "from" in from;
};

@Component
export class Item<T, S = string | number> extends Vue {
	@Prop({ required: true }) from!: ItemFrom<T, S>;
	@Prop({ required: false, type: Number, default: null }) index!: number | null;
	// The containing list injects this reset method which discards
	// all the entries of the lists and reloads them from the server.
	// Note that this does not reset the conversion from raw
	// data (say a BE's Id) to displayed item (member name, displayed URLs, …)
	// This function returns a promise that resolves once the new items have been loaded.
	@Inject({
		default: () => {
			return () => {
				throw Error("tried to reset a list from an item but this item is not contained in a list");
			};
		},
	}) protected reset!: (until?: Promise<unknown>) => Promise<void>;

	protected defaults?: Readonly<T>;

	get item(): Readonly<T> {
		if (!isIBuffered(this.from)) {
			return {
				get from() {
					throw Error();
				},
				...this.from,
			};
		}

		if (!this.isReady) {
			if (this.defaults === undefined) {
				throw new Error(`No defaults have been set for ${this.$options.name}`);
			}
			return this.defaults;
		}

		return this.from.value!;
	}

	get identifier(): S {
		if (hasIdentifier(this.from)) {
			return this.from.from;
		}

		throw Error("from does not exist in T, this item does not come from the backend but backend identifier is requested");
	}

	get isReady(): boolean {
		return !isIBuffered(this.from) || !!this.from?.value;
	}

	async invalidate(until?: Promise<unknown>): Promise<void> {
		if (isIBuffered(this.from)) {
			return this.from.invalidate(until);
		}

		if (until != null) {
			await until;
		}
	}
}

// Helper function to produce the type of Item<T, S> if it were written with Options API.
// Do not call this function, it is only meant to be used for type deduction in the ItemComponent<T, S> below.
function createNativeItem<T, S>() {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
	return defineComponent({} as {
		props: {
			from: {
				type: PropType<ItemFrom<T, S>>
				required: true
			}
			index: {
				type: PropType<number | null>
				required: false
			}
		}
		computed: {
			item(): Readonly<T>
			identifier(): S
			isReady(): boolean
		}
		methods: {
			invalidate: (until?: Promise<unknown>) => Promise<void>
			reset: (until?: Promise<unknown>) => Promise<void>
		}
	});
}

export type ItemComponent<T, S = string | number> = ReturnType<typeof createNativeItem<T, S>>;

/**
 * Helper for Vue Options API list items that need the `ItemComponent<T, S>` type.
 *
 * Use this when a component extends `Item` via `defineComponent({ extends: ... })`
 * and you want it to be typed correctly. This exists to keep `yarn lint` and the Vue type-checking
 * path happy for Options API list item components.
 */
export function createOptionsItem<T, S = string | number>(): ItemComponent<T, S> {
	// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
	return toNative(Item) as unknown as ItemComponent<T, S>;
}

export default toNative(Item) as unknown;
